import React from 'react';
import {
  ButtonWithGAClickButton,
  List,
  ListItem,
  SectionSubTitle,
  SectionTitle,
  Typography,
} from 'common/components';
import { ServicePrice } from 'modules/services';
import { createElementKey } from 'utils/create-element-key';
import { formatToRub } from 'utils/format';
import { DownloadExampleLink } from '../download-example-link';
import { DEFAULT_DESCRIPTION, ProjectTypeStructure } from './project-services-types-constants';
import {
  projectType,
  projectTypeBottom,
  projectTypeButtons,
  projectTypeComposition,
  projectTypeCompositionTitle,
  projectTypeContainer,
  projectTypeDescription,
  projectTypeExampleLink,
  projectTypePrice,
  projectTypeSchedule,
  projectTypeScheduleIcon,
  projectTypeSubtitle,
  projectTypeTitle,
  typesDescription,
  typesSection,
  typesTitle,
  projectSignButton,
} from './project-services-types-section-styles';

type ProjectServicesTypesSectionProps = {
  description?: string;
  elementBlock: string;
  onConsultationSignupBtnClick: () => void;
  projectTypeList: ProjectTypeStructure[];
  serviceTypePrice: ServicePrice | null;
};

/**
 * Вью секции с описанием типов проектных услуг
 */
export const ProjectServicesTypesSection: React.FC<ProjectServicesTypesSectionProps> = ({
  description = DEFAULT_DESCRIPTION,
  elementBlock,
  onConsultationSignupBtnClick,
  projectTypeList,
  serviceTypePrice,
}) => (
  <section className={typesSection}>
    <SectionTitle className={typesTitle}>Типы проектных услуг</SectionTitle>
    <Typography adaptive={false} className={typesDescription} variant="m" size="body_long">
      {description}
    </Typography>

    <div className={projectTypeContainer}>
      {projectTypeList.map(
        (
          {
            id,
            typeInfo,
            compositionList,
            fixedPrice,
            schedule,
            consultationSignup,
            priceUnits,
          }: ProjectTypeStructure,
          index: number
        ) => (
          <div
            className={projectType}
            itemScope
            itemType="https://schema.org/Product"
            key={createElementKey('projectType', index)}>
            <div>
              <SectionSubTitle as="h3" className={projectTypeTitle} margin="s" itemProp="name">
                {typeInfo.title}
              </SectionSubTitle>
              <Typography adaptive={false} className={projectTypeSubtitle} size="body_short" variant="m">
                {typeInfo.subTitle}
              </Typography>
              <Typography
                className={projectTypeDescription}
                itemProp="description"
                variant="s"
                size="body_long">
                {typeInfo.description}
              </Typography>
              <Typography
                adaptive={false}
                className={projectTypeCompositionTitle}
                variant="m"
                size="body_short">
                В&nbsp;услугу входит:
              </Typography>
              <List className={projectTypeComposition}>
                {compositionList.map((composition: string, compositionIndex: number) => (
                  <ListItem
                    key={createElementKey('serviceComposition', compositionIndex)}
                    text={composition}
                    variant="s"
                  />
                ))}
              </List>
            </div>

            <div className={projectTypeBottom}>
              {serviceTypePrice && serviceTypePrice[id] > 0 && (
                <div itemProp="offers" itemScope itemType="https://schema.org/Offer">
                  <SectionSubTitle className={projectTypePrice} margin="s" as="p">
                    {!fixedPrice && <span>от </span>}
                    <span itemProp="price" content={serviceTypePrice[id].toFixed(2)}>
                      {formatToRub(serviceTypePrice[id])}{' '}
                    </span>
                    <span itemProp="priceCurrency" content="RUB">
                      ₽
                    </span>
                    {priceUnits && <span>{priceUnits}</span>}
                  </SectionSubTitle>
                </div>
              )}
              <div className={projectTypeSchedule}>
                <img
                  alt=""
                  className={projectTypeScheduleIcon}
                  src="https://cdn.lemanapro.ru/lmru/image/upload/fudzi/elbrus/images/landings/engineering-systems/heating/3.svg/3.svg"
                  title=""
                />
                <Typography variant="m" size="body_long">
                  {schedule}
                </Typography>
              </div>

              <div className={projectTypeButtons}>
                <ButtonWithGAClickButton
                  className={projectSignButton}
                  elementBlock={elementBlock}
                  elementName={typeInfo.title}
                  elementText="Записаться на консультацию"
                  onClick={onConsultationSignupBtnClick}>
                  Записаться на консультацию
                </ButtonWithGAClickButton>
                <DownloadExampleLink
                  elementBlock={elementBlock}
                  elementText={typeInfo.title}
                  className={projectTypeExampleLink}
                  {...consultationSignup}
                />
              </div>
            </div>
          </div>
        )
      )}
    </div>
  </section>
);
